<script setup>
import { reactive, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useAuthStore } from '@/stores/auth'
import http from '@/api/http'
import Button from 'primevue/button'
import Password from 'primevue/password'
import Message from 'primevue/message'

const toast = useToast()
const auth = useAuthStore()

const form = reactive({
  current_password: '',
  new_password: '',
  confirm_password: '',
})

const submitting = ref(false)
const error = ref('')

function reset() {
  form.current_password = ''
  form.new_password = ''
  form.confirm_password = ''
  error.value = ''
}

async function submit() {
  error.value = ''
  if (!form.current_password || !form.new_password) {
    error.value = 'Заполните все поля'
    return
  }
  if (form.new_password.length < 8) {
    error.value = 'Новый пароль должен быть не короче 8 символов'
    return
  }
  if (form.new_password !== form.confirm_password) {
    error.value = 'Подтверждение не совпадает'
    return
  }
  submitting.value = true
  try {
    await http.post('/auth/password', {
      current_password: form.current_password,
      new_password: form.new_password,
    })
    toast.add({ severity: 'success', summary: 'Пароль обновлён', life: 3000 })
    reset()
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Ошибка'
  } finally {
    submitting.value = false
  }
}

function fmtDate(s) {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('ru-RU')
  } catch (_) {
    return s
  }
}
</script>

<template>
  <div class="page profile">
    <div class="grid">
      <div class="card" v-if="auth.user">
        <h3 class="card__title">Профиль</h3>
        <div class="row"><span class="label">Имя</span><span>{{ auth.user.name || '—' }}</span></div>
        <div class="row"><span class="label">E-mail</span><span>{{ auth.user.email }}</span></div>
        <div class="row"><span class="label">Роль</span><span>{{ auth.user.role }}</span></div>
        <div class="row">
          <span class="label">Создан</span>
          <span>{{ fmtDate(auth.user.date_create) }}</span>
        </div>
        <div class="row">
          <span class="label">Последний вход</span>
          <span>{{ fmtDate(auth.user.last_login) }}</span>
        </div>
      </div>

      <form class="card" @submit.prevent="submit">
        <h3 class="card__title">Смена пароля</h3>
        <div class="row col">
          <label for="cur">Текущий пароль</label>
          <Password
            id="cur"
            v-model="form.current_password"
            :feedback="false"
            toggle-mask
            fluid
            input-class="w-full"
            autocomplete="current-password"
          />
        </div>
        <div class="row col">
          <label for="new">Новый пароль</label>
          <Password
            id="new"
            v-model="form.new_password"
            toggle-mask
            fluid
            input-class="w-full"
            autocomplete="new-password"
          />
        </div>
        <div class="row col">
          <label for="confirm">Повторите новый</label>
          <Password
            id="confirm"
            v-model="form.confirm_password"
            :feedback="false"
            toggle-mask
            fluid
            input-class="w-full"
            autocomplete="new-password"
          />
        </div>
        <Message v-if="error" severity="error" :closable="false">{{ error }}</Message>
        <div class="actions">
          <Button label="Сбросить" text severity="secondary" @click="reset" :disabled="submitting" />
          <Button type="submit" label="Сменить пароль" icon="pi pi-key" :loading="submitting" />
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 1rem;
  align-items: start;
}
@media (max-width: 800px) {
  .grid { grid-template-columns: 1fr; }
}

.card {
  background: #fff;
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card__title {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
}

.row {
  display: flex;
  gap: 1rem;
}
.row.col { flex-direction: column; gap: 0.375rem; }
.row > label { font-size: 0.85rem; color: #4b5563; }
.label {
  width: 140px;
  color: #6b7280;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

:deep(.p-password) { width: 100%; }
:deep(.p-password input) { width: 100%; }
</style>
